import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router";

import type { EventFilterScope } from "@/features/events/types/event-filter-scope";

import {
	useScopedEventsDispatch,
	useScopedEventsState,
} from "@/context/event-filters-registry";
import { hydrateEventsFilters } from "@/context/events";
import {
	applyFiltersToSearchParams,
	type FilterUrlSyncOptions,
	parseUrlFilterUpdates,
	type UrlSyncedFilterState,
} from "@/features/events/utils/filter-url-params";
import {
	useLocationZoneSensorIdSets,
	useOrgLocationTree,
} from "@/hooks/useOrgLocationTree";

export type UseFilterUrlSyncOptions = FilterUrlSyncOptions & {
	enabled?: boolean;
	scope: EventFilterScope;
};

/** Syncs one page's event filters with URL search params. */
export function useFilterUrlSync(options: UseFilterUrlSyncOptions) {
	const { enabled = true, includeAlertStatus = false, scope } = options;
	const { pathname } = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const dispatch = useScopedEventsDispatch(scope);
	const { isLoadingLocations } = useOrgLocationTree();
	const idSets = useLocationZoneSensorIdSets();
	const {
		dateRangeFilter,
		eventTypesFilter,
		locationsFilter,
		searchQueryFilter,
		sensorsFilter,
		severityFilter,
		statusFilter,
		zonesFilter,
	} = useScopedEventsState(scope);

	const initializedRef = useRef(false);
	const hydrationRouteRef = useRef({ enabled, pathname });
	const treeReady = !isLoadingLocations;

	useLayoutEffect(() => {
		const routeChanged =
			hydrationRouteRef.current.enabled !== enabled ||
			hydrationRouteRef.current.pathname !== pathname;

		if (routeChanged) {
			hydrationRouteRef.current = { enabled, pathname };
			initializedRef.current = false;
		}

		if (!enabled || initializedRef.current || !treeReady) return;
		initializedRef.current = true;

		const updates = parseUrlFilterUpdates(searchParams, {
			includeAlertStatus,
			validIdSets: idSets,
		});

		if (Object.keys(updates).length > 0) {
			hydrateEventsFilters(dispatch, updates);
		}
	}, [
		dispatch,
		enabled,
		idSets,
		includeAlertStatus,
		pathname,
		searchParams,
		treeReady,
	]);

	useEffect(() => {
		if (!enabled || !initializedRef.current || !treeReady) return;

		const filters: UrlSyncedFilterState = {
			dateRangeFilter,
			eventTypesFilter,
			locationsFilter,
			searchQueryFilter,
			sensorsFilter,
			severityFilter,
			statusFilter,
			zonesFilter,
		};

		const nextParams = new URLSearchParams(searchParams);
		applyFiltersToSearchParams(nextParams, filters, idSets, {
			includeAlertStatus,
		});

		if (nextParams.toString() === searchParams.toString()) {
			return;
		}

		setSearchParams(nextParams, { replace: true });
	}, [
		dateRangeFilter,
		enabled,
		eventTypesFilter,
		idSets,
		includeAlertStatus,
		locationsFilter,
		searchQueryFilter,
		searchParams,
		sensorsFilter,
		severityFilter,
		setSearchParams,
		statusFilter,
		treeReady,
		zonesFilter,
	]);
}
